<template>
    <div>
        <limitless-page-header
            :loading="!initialized && busy_loading"
            title="Beställningar"
            :sub-title="(!this.not_found ? (this.id > 0 ? 'SHT' + String(this.id).padStart(6, '0') : 'Nytt inköp') : '') + (this.supplier ? ' - Leverantör: ' + this.supplier.Name : '')"
        >
            <template #header-elements>
                <limitless-page-header-button
                    v-if="supplier && supplier.ProdSupplierID"
                    :route="{ name: 'suppliers.edit', params: { id: supplier.ProdSupplierID } }"
                    icon="icon-truck"
                >Till leverantör</limitless-page-header-button>
            </template>
        </limitless-page-header>
        <div class="content" v-if="!initialized && busy_loading">
            <i class="icon-spinner2 spinner"></i>
        </div>
        <div class="content" v-else-if="!not_found && purchase && supplier">
            <div class="mb-3" v-if="purchase.ProdPurchaseID > 0">
                <button type="button" class="btn btn-secondary btn-sm mr-2 btn-labeled btn-labeled-left" @click="$bvModal.show('modal-send')"><b><i class="icon-paperplane"></i></b>Skicka beställning</button>
                <limitless-button
                    type="button"
                    ref="btnconfirmpurchase"
                    btn-class="btn-secondary"
                    class="btn-sm"
                    icon="icon-checkmark4"
                    :disabled="purchase.StatusID < 50"
                    @click="confirmPurchase"
                    saved-text="BEKRÄFTAD"
                >
                    {{ (!(purchase.ConfirmedTS) ? 'Bekräfta beställning' : 'Bekräftad') }}
                </limitless-button>
            </div>
            <div class="row">
                <div class="col-12">
                    <form @submit.prevent="savePurchase">
                        <ValidationObserver ref="orderform">
                            <div class="card">
                                <div class="card-header header-elements-inline bg-light">
                                    <h6 class="card-title"><i class="icon-pencil mr-2"></i> <span class="text-semibold">Redigera beställning</span></h6>
                                    <div class="header-elements" style="width:250px; display: block !important;">
                                        <ValidationProvider vid="StatusID" name="Status" rules="required" v-slot="{ errors }">
                                            <v-select
                                                v-model="purchase.StatusID"
                                                :options="statuses"
                                                :reduce="s => s.id"
                                                :clearable="false"
                                                label="name"
                                            ></v-select>
                                            <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                                        </ValidationProvider>
                                    </div>
                                </div>
                                <div class="card-body">
                                    <div class="alert alert-info" v-if="purchase.ProdPurchaseID > 0 && purchase.StatusID < 50">Beställningen är inte skickad till leverantör</div>
                                    <div class="alert alert-warning" v-if="purchase.ProdPurchaseID > 0 && purchase.StatusID >= 50 && !purchase.ConfirmedTS">Leverantören har inte bekräftat beställningen</div>
                                    <div class="alert alert-info" v-if="price_diffs.length > 0">
                                        <div class="mb-2"><b>Inköpspriset skiljer sig mot Newport för följande artiklar:</b></div>
                                        <table class="table table-sm table-borderless mb-2">
                                            <thead>
                                                <tr>
                                                    <th>Artikel</th>
                                                    <th>Lev. artnr</th>
                                                    <th class="text-right">Nuvarande pris</th>
                                                    <th class="text-right">Pris hos Newport</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="diff in price_diffs" :key="diff.prod_id">
                                                    <td>{{ diff.prod_id }} - {{ diff.prod_name }}</td>
                                                    <td><a v-if="is_nph_supplier" :href="nphProductUrl(diff.prod_supplier_sku)" target="_blank">{{ diff.prod_supplier_sku }}</a><template v-else>{{ diff.prod_supplier_sku }}</template></td>
                                                    <td class="text-right">{{ diff.current_prod_net_price }}</td>
                                                    <td class="text-right">{{ diff.nph_prod_net_price }}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                        <limitless-button
                                            type="button"
                                            ref="btnupdateprices"
                                            btn-class="btn-info"
                                            class="btn-sm"
                                            icon="icon-loop3"
                                            :disabled="busy_updating_prices"
                                            @click="updatePrices"
                                        >
                                            Uppdatera priser
                                        </limitless-button>
                                    </div>
                                    <div class="row">
                                        <div class="col-lg-7">
                                            <div class="form-group row">
                                                <label class="col-form-label col-lg-2">Ordernummer</label>
                                                <div v-if="purchase.ProdPurchaseID > 0" class="col-lg-5">
                                                    <div><b>SHT{{ String(purchase.ProdPurchaseID).padStart(6, '0') }}</b></div>
                                                    <div>Skapad: {{ formatDateTime(purchase.CreatedTS).substring(0, 16) }}</div>
                                                    <div v-if="purchase.ModifiedTS">Ändrad: {{ formatDateTime(purchase.ModifiedTS).substring(0, 16) }}<span v-if="purchase.modified_by"> / {{ purchase.modified_by.FullName }}</span></div>
                                                    <div v-if="purchase.SentTS">Skickad: {{ formatDateTime(purchase.SentTS).substring(0, 16) }}<span v-if="purchase.sent_by"> / {{ purchase.sent_by.FullName }}</span></div>
                                                </div>
                                                <div v-else class="col-lg-5">
                                                    <b>Ny</b>
                                                </div>
                                            </div>
                                            <div class="form-group row">
                                                <label class="col-form-label col-lg-2">Ansvarig inköpare</label>
                                                <div class="col-lg-5">
                                                    <ValidationProvider vid="UserID" name="Ansvarig inköpare" rules="required" v-slot="{ errors }">
                                                        <v-select
                                                            v-model="purchase.UserID"
                                                            :options="select_users"
                                                            :reduce="s => s.id"
                                                            :clearable="false"
                                                            :get-option-label="(user) => user.first_name + ' ' + user.last_name"
                                                        ></v-select>
                                                        <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                                                    </ValidationProvider>
                                                </div>
                                            </div>
                                            <div class="form-group row">
                                                <label class="col-form-label col-lg-2">Leveransadress</label>
                                                <div class="col-lg-5">
                                                    <ValidationProvider vid="AddressID" name="Leveransadress" rules="required" v-slot="{ errors }">
                                                        <address-filter
                                                            v-model="purchase.AddressID"
                                                            :reduce="a => a.id"
                                                            :clearable="false"
                                                        ></address-filter>
                                                        <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                                                    </ValidationProvider>
                                                </div>
                                            </div>
                                            <div class="form-group row">
                                                <label class="col-form-label col-lg-2">Önskat leveransdatum</label>
                                                <div class="col-lg-5">
                                                    <ValidationProvider vid="WishedDeliveryTS" name="Önskat leveransdatum" rules="" v-slot="{ errors }">
                                                        <v-date-picker v-model="purchase.WishedDeliveryTS" mode="date" :model-config="{ type: 'string', mask: 'iso' }" :masks="{ input: 'YYYY-MM-DD' }" show-iso-weeknumbers>
                                                            <template v-slot="{ inputValue, togglePopover }">
                                                                <input class="form-control" :value="inputValue" @focus="togglePopover()">
                                                            </template>
                                                        </v-date-picker>
                                                        <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                                                    </ValidationProvider>
                                                </div>
                                            </div>
                                            <div class="form-group row">
                                                <label class="col-form-label col-lg-2">Leverantörens ordernr</label>
                                                <div class="col-lg-5">
                                                    <ValidationProvider vid="ExtOrderID" name="Externt Order ID" rules="" v-slot="{ errors }">
                                                        <input type="text" class="form-control" v-model="purchase.ExtOrderID" :class="{'border-danger':errors[0]}">
                                                        <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                                                    </ValidationProvider>
                                                </div>
                                            </div>
                                            <div class="form-group row">
                                                <label class="col-form-label col-lg-2">Kommentar</label>
                                                <div class="col-lg-10">
                                                    <ValidationProvider vid="Kommentar" name="Kommentar" rules="" v-slot="{ errors }">
                                                        <elastic-textarea style="resize: horizontal; height: 76px;" v-model="purchase.Kommentar" class="form-control" :class="{'border-danger':errors[0]}"></elastic-textarea>
                                                        <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                                                    </ValidationProvider>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="col-lg-5">
                                            <file-list-uploader
                                                class="mb-3"
                                                model-type="supplier_purchases"
                                                :model-id="id"
                                                :extensions="['jpg','jpeg','png','pdf','mp4','doc','docx','xls','xlsx']"
                                                :accept="'image/png,image/jpeg,application/pdf,video/mp4,' +
                                                    'application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,' +
                                                    'application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'"
                                                multiple
                                                auto-start-upload
                                                display-list
                                                header="Filer/dokument"
                                            ></file-list-uploader>
                                        </div>
                                    </div>
                                </div> <!-- /card-body -->
                                <div class="card-header bg-light border-top header-elements-inline">
                                    <h6 class="card-title"><i class="icon-cart5 mr-2"></i> <span class="text-semibold">Beställning</span></h6>
                                    <div class="header-elements">
                                        <button type="button" @click="showModalCreateDelivery" class="btn btn-primary mr-2" :disabled="purchase.StatusID < 50 || purchase.StatusID >= 100">Skapa inleverans</button>
                                        <button type="button" @click="$bvModal.show('modal-set-deliv-date')" class="btn btn-light">Multi-ändra lev.datum</button>
                                    </div>
                                </div>
                                <div>
                                    <div class="d-flex flex-row border-bottom">
                                        <div style="flex: 0 0 60px;" class="px-1 py-2 font-weight-semibold">ID</div>
                                        <div style="flex: 0 0 100px;" class="px-1 py-2 font-weight-semibold">Lev.Artnr.</div>
                                        <div style="flex: 0 0 46px;" class="px-1 py-2 font-weight-semibold">Bild</div>
                                        <div style="flex-grow: 1;" class="px-1 py-2 font-weight-semibold">Produkt</div>
                                        <div v-if="purchase.StatusID >= 50" style="flex: 0 0 120px;" class="px-1 py-2 font-weight-semibold">Status</div>
                                        <div style="flex: 0 0 80px;" class="pl-1 pr-2 py-2 font-weight-semibold text-right">Á-pris</div>
                                        <div style="flex: 0 0 80px;" class="px-1 py-2 font-weight-semibold text-center">Best. Antal</div>
                                        <div v-if="purchase.StatusID >= 50" style="flex: 0 0 80px;" class="px-1 py-2 font-weight-semibold text-center">Lev. Antal</div>
                                        <div style="flex: 0 0 150px;" class="px-1 py-2 font-weight-semibold text-center">Beräknat Lev.datum</div>
                                        <div style="flex: 0 0 150px;" class="px-1 py-2 font-weight-semibold text-center">Lev. ordernr</div>
                                        <div style="flex: 0 0 58px;" class="px-1 py-2 font-weight-semibold text-right"></div>
                                        <div v-if="purchase.StatusID < 50" style="flex: 0 0 58px;" class="px-1 py-2 font-weight-semibold text-right"></div>
                                    </div>
                                    <div v-for="(item, itm_idx) in purchase.items" :key="'item-' + itm_idx" class="border-bottom"> <!--  style="border-bottom: 5px solid #ddd;" -->
                                        <div class="d-flex flex-row" :class="{ 'opacity-50': item.delete, 'bg-danger-200': item.delete }">
                                            <div v-if="item.product" style="flex: 0 0 60px;" class="p-1">
                                                <div style="line-height: 36px;">
                                                    <router-link :to="{ name: 'products.edit', params: { id: item.ProdID } }" target="_blank">{{ item.ProdID }}</router-link><MarkedProductIndicator :prod-id="item.ProdID" />
                                                </div>
                                            </div>
                                            <div v-if="item.product" style="flex: 0 0 100px;" class="p-1">
                                                <div style="line-height: 36px;">
                                                    <a v-if="is_nph_supplier" :href="nphProductUrl(item.product.ProdSupplierSKU)" target="_blank">{{ item.product.ProdSupplierSKU }}</a>
                                                    <template v-else>{{ item.product.ProdSupplierSKU }}</template>
                                                </div>
                                            </div>
                                            <div v-if="item.product" style="flex: 0 0 46px;" class="p-1">
                                                <div v-if="item.product.images && item.product.images.length > 0">
                                                    <img :id="`product-image-${item.product.ProdID}`" :src="item.product.images[0].urls['small']" :alt="item.product.ProdID" style="height: 36px; margin: 0;">
                                                    <!--                    <div v-if="item.product.images.length > 1" style="margin-top: 8px; margin-bottom: -8px;">{{ item.product.images.length }} bilder i popup</div>-->
                                                    <b-popover :target="`product-image-${item.product.ProdID}`" triggers="hover">
                                                        <img :src="item.product.images[0].urls['medium']" :alt="item.product.ProdID" style="width: 100%;">
                                                    </b-popover>
                                                </div>
                                            </div>
                                            <div style="flex-grow: 1;" class="p-1">
                                                <div v-if="item.product" style="line-height: 36px;">
                                                    {{ (item.product.ProdNameWeb ? item.product.ProdNameWeb : item.product.ProdName) }}
                                                    <span class="text-muted" v-if="item.product.SupplierPackageSize > 0">({{ item.product.SupplierPackageSize }}/fp)</span>
                                                </div>
                                                <v-select
                                                    v-else
                                                    :options="select_products"
                                                    :get-option-label="(prod) => prod.ProdID + ' - ' + (prod.ProdNameWeb ? prod.ProdNameWeb : prod.ProdName)"
                                                    placeholder="Sök produkt..."
                                                    @input="(prod) => { item.ProdID = prod.ProdID; item.product = prod; }"
                                                    :filterable="false"
                                                    @search="searchProducts"
                                                ></v-select>
                                            </div>
                                            <div v-if="item.product && purchase.StatusID >= 50" style="flex: 0 0 120px; line-height: 36px;" class="p-1">
                                                <span v-if="item.derived && item.Antal !== item.derived.Antal" class="badge badge-warning"><i class="icon-warning mr-1" style="font-size: 10px;"></i>Avvikelse</span>
                                                <span v-else-if="item.derived" class="badge badge-success">Levererad</span>
                                                <span v-else-if="item.Antal <= 0" class="badge badge-danger">Avbeställd</span>
                                                <span v-else class="badge badge-primary">Beställd</span>
                                            </div>
                                            <div v-if="item.product" style="flex: 0 0 80px;" class="p-1 pr-2 text-right">
                                                <div style="line-height: 36px;">{{ new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(item.product.ProdNetPrice) }}</div>
                                            </div>
                                            <div style="flex: 0 0 80px;" class="p-1">
                                                <ValidationProvider :vid="'items.' + itm_idx + '.Antal'" name="Antal" rules="" v-slot="{ errors }">
                                                    <input
                                                        type="number"
                                                        step="1"
                                                        class="form-control text-right"
                                                        v-model="item.Antal"
                                                        :class="{'border-danger':errors[0]}"
                                                        :disabled="item.derived && item.derived.Antal === item.Antal"
                                                    >
                                                    <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                                                </ValidationProvider>
                                            </div>
                                            <div v-if="purchase.StatusID >= 50"  style="flex: 0 0 80px; line-height: 36px;" class="p-1 text-right">
                                                {{ (item.derived ? item.derived.Antal : '-') }}
                                            </div>
                                            <div style="flex: 0 0 150px;" class="p-1">
                                                <ValidationProvider :vid="'items.' + itm_idx + '.EffectiveTS'" name="Beräknat Lev.datum" rules="" v-slot="{ errors }">
                                                    <input v-if="item.derived && item.derived.Antal === item.Antal" type="text" class="form-control" :value="item.derived.EffectiveTS.substring(0,10)" disabled>
                                                    <v-date-picker v-else v-model="item.EffectiveTS" mode="date" :model-config="{ type: 'string', mask: 'YYYY-MM-DD' }" :masks="{ input: 'YYYY-MM-DD' }" show-iso-weeknumbers>
                                                        <template v-slot="{ inputValue, togglePopover }">
                                                            <div class="input-group">
                                                                <input class="form-control" :value="inputValue" @focus="togglePopover()">
                                                                <span class="input-group-append">
                                                                    <button class="btn btn-light px-2" type="button" @click="item.EffectiveTS = ''"><i class="icon-cross3"></i></button>
                                                                </span>
                                                            </div>
                                                        </template>
                                                    </v-date-picker>
                                                    <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                                                </ValidationProvider>
                                            </div>
                                            <div style="flex: 0 0 150px;" class="p-1">
                                                <ValidationProvider :vid="'items.' + itm_idx + '.ExtOrderID'" name="Ext. OrderID" rules="" v-slot="{ errors }">
                                                    <input type="text" class="form-control text-right" v-model="item.ExtOrderID" :class="{'border-danger':errors[0]}">
                                                    <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                                                </ValidationProvider>
                                            </div>
                                            <div style="flex: 0 0 58px;" class="p-1 text-right">
                                                <button type="button" class="btn btn-warning px-2" @click="splitItem(item)" :disabled="item.derived && item.derived.Antal === item.Antal || item.Antal < 2">Split</button>
                                            </div>
                                            <div v-if="purchase.StatusID < 50" style="flex: 0 0 58px;" class="p-1 text-right">
                                                <button type="button" class="btn btn-danger btn-block px-2" @click="deleteItem(itm_idx)" :disabled="item.derived"><i class="icon-trash"></i></button>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="p-1 text-right font-size-lg font-weight-bold">
                                        Totalt: {{ order_total }}
                                    </div>
                                    <div class="p-1 border-top" v-if="purchase.StatusID < 50">
                                        <button type="button" class="btn btn-light btn-block" @click="addOrderItem"><i class="icon-plus3 mr-2"></i>Lägg till rad</button>
                                    </div>
                                </div>
                                <div class="card-footer">
                                    <div class="d-flex justify-content-end">
                                        <limitless-button
                                            ref="btndeleteorder"
                                            type="button"
                                            @click="deletePurchase"
                                            btn-class="btn-danger"
                                            :busy="busy_deleting"
                                            icon="icon-trash"
                                            :disabled="busy_saving || purchase.StatusID >= 50"
                                            class="mr-auto"
                                        >
                                            Radera
                                        </limitless-button>
                                        <limitless-button
                                            ref="btnsaveorder"
                                            type="button"
                                            @click="savePurchase"
                                            :busy="busy_saving"
                                            icon="icon-floppy-disk"
                                            :disabled="busy_deleting"
                                        >
                                            SPARA
                                        </limitless-button>
                                    </div>
                                </div>
                            </div> <!-- /card -->
                        </ValidationObserver>
                    </form>
                </div> <!-- /col -->
            </div> <!-- /row -->
            <div class="row">
                <div class="col-12">
                    <div class="card">
                        <div class="card-header bg-light header-elements-inline">
                            <h6 class="card-title"><i class="icon-file-pdf mr-2"></i> <span class="text-semibold">Inköpsorder</span></h6>
                            <div class="header-elements">
                                <limitless-button
                                    btn-class="btn-light"
                                    icon="icon-file-pdf"
                                    @click="generatePdf"
                                    :busy="busy_generating_pdf"
                                    ref="btngeneratepdf"
                                    saved-text="KLAR!"
                                >
                                    Generera om
                                </limitless-button>
                            </div>
                        </div>
                        <div class="card-body">
                            <iframe v-if="purchase.pdf_url" :src="purchase.pdf_url" style="width: 100%; height: 850px;"></iframe>
                            <i v-else-if="busy_generating_pdf">Var god vänta...</i>
                            <i v-else>Ingen PDF...</i>
                        </div>
                    </div>
                </div> <!-- /col -->
            </div> <!-- /row -->

            <b-modal id="modal-send" title="Skicka beställning" size="lg" @ok="sendPurchase">
                <div v-if="!(supplier.Email)" class="alert alert-danger">
                    Leverantören saknar e-postadress!
                </div>
                <div class="form-group row" v-if="purchase.SentTS">
                    <label class="col-form-label col-lg-2 pt-0">Senast skickad</label>
                    <div class="col-lg-10">
                        {{ formatDateTime(purchase.SentTS).substring(0, 16) }}<span v-if="purchase.sent_by"> / {{ purchase.sent_by.FullName }}</span>
                    </div>
                </div>
                <div v-if="!purchase.pdf_url" class="alert alert-warning alert-styled-left">Ingen PDF genererad...</div>
                <div class="form-group row">
                    <label class="col-form-label col-lg-2 pt-0">PDF</label>
                    <div class="col-lg-10">
                        <div class="custom-control custom-checkbox mb-2">
                            <input type="checkbox" class="custom-control-input" id="chk-attach-pdf" v-model="send.pdf">
                            <label class="custom-control-label" for="chk-attach-pdf">Bifoga PDF i mailet</label>
                        </div>
                    </div>
                </div>
                <div class="form-group row">
                    <label class="col-form-label col-lg-2">Meddelande</label>
                    <div class="col-lg-10">
                        <textarea rows="5" class="form-control" placeholder="Personligt meddelande till leverantören" v-model="send.message"></textarea>
                    </div>
                </div>
                <div class="form-group row">
                    <label class="col-form-label col-lg-2">Skickas från</label>
                    <div class="col-lg-10">
                        <input type="text" class="form-control" :value="( user ? user.name + ' (' + user.work_email + ')' : '' )" disabled>
                    </div>
                </div>
                <div class="form-group row">
                    <label class="col-form-label col-lg-2">Skickas till</label>
                    <div class="col-lg-10">
                        <input type="text" class="form-control" :value="supplier.Name + ' (' + supplier.Email + ')'" disabled>
                    </div>
                </div>
                <template #modal-footer="{ cancel, ok }">
                    <button type="button" class="btn btn-light" @click="cancel">Avbryt</button>
                    <limitless-button ref="btnsendpurchase" type="button" @click="ok" icon="icon-paperplane" :disabled="!(supplier.Email)">Skicka</limitless-button>
                </template>
            </b-modal>

            <b-modal title="Sätt beräknat lev.datum" id="modal-set-deliv-date" size="sm" @ok="setDeliveryDate">
                <v-date-picker v-model="new_deliv_date" mode="date" :model-config="{ type: 'string', mask: 'YYYY-MM-DD' }" :masks="{ input: 'YYYY-MM-DD' }" show-iso-weeknumbers>
                    <template v-slot="{ inputValue, togglePopover }">
                        <input class="form-control" :value="inputValue" @focus="togglePopover()">
                    </template>
                </v-date-picker>
                <template #modal-footer="{ cancel, ok }">
                    <button type="button" class="btn btn-light" @click="cancel">Avbryt</button>
                    <limitless-button ref="btnsetdate" type="button" @click="ok" icon="icon-floppy-disk" :disabled="!(supplier.Email)">Sätt datum</limitless-button>
                </template>
            </b-modal>

            <b-modal title="Skapa inleverans" id="modal-create-delivery" size="xl" @ok="createDelivery">
                <ValidationObserver ref="deliveryform">
                    <table class="table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Lev.Artnr.</th>
                                <th>Bild</th>
                                <th>Produkt</th>
                                <th>Status</th>
                                <th>Best. Antal</th>
                                <th class="px-0"></th>
                                <th>Inleverans</th>
                                <th>Lev.datum</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, itm_idx) in purchase.items.filter((item) => item.product)" :key="'deliv-item-' + itm_idx" class="border-bottom">
                                <td>{{ item.ProdID }}<MarkedProductIndicator :prod-id="item.ProdID" /></td>
                                <td><a v-if="is_nph_supplier" :href="nphProductUrl(item.product.ProdSupplierSKU)" target="_blank">{{ item.product.ProdSupplierSKU }}</a><template v-else>{{ item.product.ProdSupplierSKU }}</template></td>
                                <td v-if="item.product">
                                    <div v-if="item.product.images && item.product.images.length > 0">
                                        <img :id="`deliv-product-image-${item.product.ProdID}`" :src="item.product.images[0].urls['small']" :alt="item.product.ProdID" style="height: 36px; margin: 0;">
                                        <!--                    <div v-if="item.product.images.length > 1" style="margin-top: 8px; margin-bottom: -8px;">{{ item.product.images.length }} bilder i popup</div>-->
                                        <b-popover :target="`deliv-product-image-${item.product.ProdID}`" triggers="hover">
                                            <img :src="item.product.images[0].urls['medium']" :alt="item.product.ProdID" style="width: 100%;">
                                        </b-popover>
                                    </div>
                                </td>
                                <td>{{ (item.product.ProdNameWeb ? item.product.ProdNameWeb : item.product.ProdName) }}</td>
                                <td>
                                    <span v-if="item.derived && item.Antal !== item.derived.Antal" class="badge badge-warning"><i class="icon-warning mr-1" style="font-size: 10px;"></i>Avvikelse</span>
                                    <span v-else-if="item.derived" class="badge badge-success">Levererad</span>
                                    <span v-else-if="item.Antal <= 0" class="badge badge-danger">Avbeställd</span>
                                    <span v-else class="badge badge-primary">Beställd</span>
                                </td>
                                <td>
                                    {{ item.Antal }}
                                </td>
                                <td class="px-0">
                                    <button v-if="!item.derived" type="button" class="btn btn-link" @click="delivery.items[itm_idx].quantity = item.Antal"><i class="icon-arrow-right6"></i></button>
                                </td>
                                <td>
                                    <ValidationProvider :vid="'items.' + itm_idx + '.Antal'" name="Antal" rules="" v-slot="{ errors }">
                                        <input
                                            type="number"
                                            step="1"
                                            class="form-control text-right"
                                            v-model="delivery.items[itm_idx].quantity"
                                            :class="{'border-danger':errors[0]}"
                                            :disabled="item.derived"
                                            style="width: 80px;"
                                        >
                                        <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                                    </ValidationProvider>
                                </td>
                                <td>
                                    <ValidationProvider :vid="'items.' + itm_idx + '.delivery_date'" name="Lev.datum" rules="" v-slot="{ errors }">
                                        <v-date-picker
                                            v-model="delivery.items[itm_idx].delivery_date"
                                            mode="date"
                                            :model-config="{ type: 'string', mask: 'YYYY-MM-DD' }"
                                            :masks="{ input: 'YYYY-MM-DD' }"
                                            show-iso-weeknumbers
                                        >
                                            <template v-slot="{ inputValue, togglePopover }">
                                                <input
                                                    style="width: 110px;"
                                                    class="form-control"
                                                    :value="inputValue"
                                                    @focus="togglePopover()"
                                                    :disabled="item.derived"
                                                >
                                            </template>
                                        </v-date-picker>
                                        <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                                    </ValidationProvider>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div class="row mt-3">
                        <div class="col-lg-6">
                            <div class="form-group">
                                <label>Kommentar</label>
                                <ValidationProvider vid="comment" name="Kommentar" rules="" v-slot="{ errors }">
                                    <elastic-textarea style="resize: horizontal; height: 76px;" v-model="delivery.comment" class="form-control" :class="{'border-danger':errors[0]}"></elastic-textarea>
                                    <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                                </ValidationProvider>
                            </div>
                        </div>
                    </div>
                </ValidationObserver>

                <template #modal-footer="{ cancel, ok }">
                    <button type="button" class="btn btn-light" @click="cancel">Avbryt</button>
                    <limitless-button ref="btncreatedelivery" type="button" @click="ok" icon="icon-floppy-disk" :busy="busy_creating_delivery">Skapa inleverans</limitless-button>
                </template>
            </b-modal>

            <activity-notification v-if="purchase && purchase.ProdPurchaseID > 0" :channel="'supplier-purchase-' + purchase.ProdPurchaseID"></activity-notification>
        </div>
        <div class="content" v-else-if="not_found">
            <i>Beställningen hittades inte</i>
        </div>
        <div class="content" v-else>
            <i>Något gick fel...</i>
        </div>
    </div>
</template>

<script>
import apiClient from '@services/ApiClient';
import LimitlessButton from '@components/Limitless/LimitlessButton';
import LimitlessPageHeader from '@components/Layout/partials/LimitlessPageHeader';
import LimitlessPageHeaderButton from '@components/Layout/partials/LimitlessPageHeaderButton';
import ActivityNotification from '@components/User/ActivityNotification';
import ElasticTextarea from '@components/ElasticTextarea';
import vSelect from 'vue-select';
import VDatePicker from 'v-calendar/lib/components/date-picker.umd';
import {
    formatDateTime, genUUID,
} from '@services/Helpers';
import getError from '@services/ErrorHelper';
import { debounce } from 'lodash';
import PurchaseStatusRepository from '@services/repositories/Purchase/PurchaseStatusRepository';
import UserRepository from '@services/repositories/UserRepository';
import { mapState } from 'pinia';
import AddressFilter from '@components/Filters/AddressFilter';
import { useAuthStore } from '@services/pinia/AuthStore';
import FileListUploader
    from '@components/Form/FileListUploader.vue';

const NPH_SUPPLIER_IDS = [25, 121];

export default {
    name: 'PurchaseEditor',
    components: {
        FileListUploader,
        AddressFilter,
        ElasticTextarea,
        LimitlessButton,
        LimitlessPageHeader,
        LimitlessPageHeaderButton,
        ActivityNotification,
        vSelect,
        VDatePicker,
    },
    props: {
        id: {
            type: [Number, String],
            default: 0,
        },
    },
    data: () => ({
        not_found: false,
        initialized: false,
        busy_loading: true,
        busy_saving: false,
        busy_deleting: false,
        busy_creating_delivery: false,
        busy_generating_pdf: false,
        busy_updating_prices: false,
        price_diffs: [],
        purchase: null,
        supplier: null,
        statuses: [],
        users: [],
        select_products: [],
        send: {
            message: '',
            pdf: true,
        },
        new_deliv_date: null,
        delivery: {
            comment: null,
            items: [],
        },
    }),
    mounted() {
        this.getStatuses();
        this.getUsers();
        this.init();
    },
    watch: {
        id() {
            this.init();
        },
    },
    methods: {
        formatDateTime,
        init() {
            this.getPurchase();
        },
        async getStatuses() {
            const client = new PurchaseStatusRepository();
            this.statuses = await client.getAll();
        },
        getUsers() {
            const client = new UserRepository();
            client.getAll().then((res) => {
                this.users = res;
            });
        },
        async getPurchase(keepCurrentOrderData = false) {
            this.busy_loading = true;
            await apiClient.get('/vue/suppliers/purchases/' + this.id + (this.supplierId ? '?supplier_id=' + this.supplierId : ''))
                .then((response) => {
                    if (!keepCurrentOrderData) {
                        this.purchase = response.data.purchase;
                    }
                    this.supplier = response.data.supplier;
                    this.price_diffs = response.data.price_diffs || [];
                })
                .catch((error) => {
                    const { status } = error.response;
                    if (status === 404) {
                        this.not_found = true;
                    }
                })
                .finally(() => {
                    this.initialized = true;
                    this.busy_loading = false;
                });
        },
        async savePurchase() {
            const isValid = await this.$refs.orderform.validate();
            if (isValid) {
                this.busy_saving = true;
                await apiClient.post('/vue/suppliers/purchases/' + this.id, { ...this.purchase })
                    .then((response) => {
                        if (parseInt(this.id, 10) !== parseInt(response.data.purchase.ProdPurchaseID, 10)) {
                            window.location.href = '/suppliers/purchases/' + response.data.purchase.ProdPurchaseID;
                            window.location.href = '/suppliers/purchases/' + response.data.purchase.ProdPurchaseID;
                        }
                        this.purchase = response.data.purchase;
                        this.supplier = response.data.supplier;
                        this.price_diffs = response.data.price_diffs || [];
                        this.$refs.btnsaveorder.triggerSaved();
                    }).catch((error) => {
                        const parsederror = getError(error);
                        if (typeof parsederror === 'object') {
                            this.$refs.orderform.setErrors(parsederror);
                        }
                        this.$refs.btnsaveorder.triggerFailed();
                    }).finally(() => {
                        this.busy_saving = false;
                    });
            } else {
                this.$refs.btnsaveorder.triggerFailed();
            }
        },
        nphProductUrl(sku) {
            return 'https://interstellar.newport.se/products/' + encodeURIComponent(String(sku).trim());
        },
        async updatePrices() {
            this.busy_updating_prices = true;
            const products = {};
            this.price_diffs.forEach((diff) => {
                products[diff.prod_id] = { prod_net_price: diff.nph_prod_net_price };
            });
            await apiClient.post('/vue/products/multiupdate', { products })
                .then(() => {
                    this.purchase.items.forEach((item) => {
                        if (item.product && products[item.ProdID]) {
                            item.product.ProdNetPrice = products[item.ProdID].prod_net_price;
                        }
                    });
                    this.$refs.btnupdateprices.triggerSaved();
                    this.getPurchase(true);
                }).catch(() => {
                    this.$refs.btnupdateprices.triggerFailed();
                }).finally(() => {
                    this.busy_updating_prices = false;
                });
        },
        async deletePurchase() {
            let doDelete = false;
            await this.$swal({
                text: 'Är du säker att du vill radera denna beställning?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Ja, radera!',
            }).then((result) => {
                if (result.isConfirmed) {
                    doDelete = true;
                }
            });
            if (doDelete) {
                this.busy_deleting = true;
                await apiClient.delete('/vue/suppliers/purchases/' + this.id)
                    .then(() => {
                        window.location.href = '/suppliers/' + this.purchase.ProdSupplierID;
                        this.$refs.btndeleteorder.triggerSaved();
                    }).catch((error) => {
                        const parsederror = getError(error);
                        if (typeof parsederror === 'object') {
                            this.$refs.orderform.setErrors(parsederror);
                        }
                        this.$refs.btndeleteorder.triggerFailed();
                    }).finally(() => {
                        this.busy_deleting = false;
                    });
            }
        },
        async confirmPurchase() {
            let doConfirm = false;
            await this.$swal({
                text: 'Är du säker att du vill bekräfta denna beställning?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Ja, bekräfta!',
            }).then((result) => {
                if (result.isConfirmed) {
                    doConfirm = true;
                }
            });
            if (doConfirm) {
                await apiClient.post('/vue/suppliers/purchases/' + this.id + '/confirm')
                    .then((response) => {
                        if (response.data.purchase) {
                            this.purchase.ConfirmedTS = response.data.purchase.ConfirmedTS;
                        }
                        this.$refs.btnconfirmpurchase.triggerSaved();
                    }).catch(() => {
                        this.$refs.btnconfirmpurchase.triggerFailed();
                    });
            }
        },
        async generatePdf() {
            this.busy_generating_pdf = true;
            this.purchase.pdf_url = '';
            await apiClient.post('/vue/suppliers/purchases/' + this.id + '/generate_pdf')
                .then((response) => {
                    if (response.data && response.data.url) {
                        this.purchase.pdf_url = response.data.url + '?hash=' + genUUID();
                    }
                }).catch(() => {
                    this.$refs.btngeneratepdf.triggerFailed();
                }).finally(() => {
                    this.busy_generating_pdf = false;
                });
        },
        async sendPurchase(e) {
            e.preventDefault();
            await apiClient.post('/vue/suppliers/purchases/' + this.id + '/send', { ...this.send })
                .then((response) => {
                    this.purchase = response.data.purchase;
                    this.supplier = response.data.supplier;
                    this.price_diffs = response.data.price_diffs || [];
                    this.$bvModal.hide('modal-send');
                }).catch((error) => {
                    this.$refs.btnsendpurchase.triggerFailed();
                    let message = 'Beställningen kunde ej skickas.';
                    if (error.response && error.response.data && error.response.data.message) {
                        message = error.response.data.message;
                    }
                    this.$swal({
                        title: 'Något gick fel...',
                        text: message,
                        icon: 'error',
                        timer: 3000,
                    });
                });
        },
        addOrderItem() {
            this.purchase.items.push({
                RowID: null,
                CreatedTS: null,
                EffectiveTS: null,
                ProdPurchaseID: null,
                ProdID: null,
                Antal: 1,
                Typ: 1,
                Kommentar: '',
                ExtOrderID: '',
                UserID: 0,
                DerivedRowID: '',
                isDeprecated: 0,
                isChecked: 0,
                PurchaseID: null,
                product: null,
            });
        },
        searchProducts: debounce(async function searchProducts(search) {
            await apiClient.get('/vue/products', {
                params: {
                    search,
                    per_page: 8,
                    supplier_id: this.supplier.ProdSupplierID,
                    is_struct: 0,
                },
            }).then((response) => {
                this.select_products = response.data.data;
            });
        }, 300),
        splitItem(orgItem) {
            const item = { ...orgItem };
            item.derived = null;
            item.RowID = null;
            if (orgItem.derived) {
                item.Antal = orgItem.Antal - orgItem.derived.Antal;
                orgItem.Antal = orgItem.derived.Antal;
            } else {
                item.Antal = Math.floor(orgItem.Antal / 2);
                orgItem.Antal = Math.ceil(orgItem.Antal / 2);
            }
            this.purchase.items.push(item);
        },
        deleteItem(idx) {
            if (this.purchase.items[idx].delete) {
                this.purchase.items[idx].delete = false;
            } else {
                this.purchase.items[idx].delete = true;
            }
        },
        setDeliveryDate() {
            if (this.new_deliv_date) {
                this.purchase.items.forEach((item) => {
                    if (!item.derived && item.Antal > 0) {
                        item.EffectiveTS = this.new_deliv_date;
                    }
                });
            }
        },
        showModalCreateDelivery() {
            const prom = new Promise((resolve) => {
                const date = (new Date()).toISOString().substring(0, 10);
                this.delivery.comment = null;
                this.delivery.items = [];
                this.purchase.items.forEach((item) => {
                    this.delivery.items.push({
                        row_id: item.RowID,
                        quantity: 0,
                        delivery_date: date,
                    });
                });
                resolve();
            });

            prom.then(() => {
                this.$bvModal.show('modal-create-delivery');
            });
        },
        async createDelivery(e) {
            e.preventDefault();
            const isValid = await this.$refs.deliveryform.validate();
            if (isValid) {
                this.busy_creating_delivery = true;
                await apiClient.post('/vue/suppliers/purchases/' + this.id + '/deliver', this.delivery)
                    .then(() => {
                        this.$bvModal.hide('modal-create-delivery');
                        this.getPurchase();
                    }).catch((error) => {
                        const parsederror = getError(error);
                        if (typeof parsederror === 'object') {
                            this.$refs.deliveryform.setErrors(parsederror);
                        }
                        this.$refs.btncreatedelivery.triggerFailed();
                    }).finally(() => {
                        this.busy_creating_delivery = false;
                    });
            } else {
                this.$refs.btncreatedelivery.triggerFailed();
            }
        },
    },
    computed: {
        ...mapState(useAuthStore, ['user']),
        is_nph_supplier() {
            return !!this.supplier && NPH_SUPPLIER_IDS.includes(parseInt(this.supplier.ProdSupplierID, 10));
        },
        old_system_url() {
            return window.config.app.old_system_url;
        },
        order_total() {
            let sum = 0;
            if (this.purchase && this.purchase.items && Array.isArray(this.purchase.items)) {
                this.purchase.items.forEach((item) => {
                    if (item.product) {
                        sum += item.Antal * item.product.ProdNetPrice;
                    }
                });
            }
            return new Intl.NumberFormat('sv-SE', {
                style: 'currency',
                currency: 'SEK',
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
            }).format(sum);
        },
        select_users() {
            if (this.users) {
                return this.users.filter((user) => !user.deleted_at || (this.purchase && user.id === this.purchase.UserID));
            }
            return [];
        },
    },
};
</script>
